/**
 * Cómo se registró el grabador.
 *
 * `automatico` es el caso normal: se guarda su RTSP y el agente del servidor
 * crea el camino en el servidor de vídeo. `manual` existe para los grabadores
 * que ya estaban publicados a mano antes de que existiera el agente.
 */
export type ModoGrabador = 'automatico' | 'manual';

/**
 * Une el enlace base del grabador con el canal de la cámara.
 *
 * El grabador guarda el enlace sin el número final —`…/Streaming/Channels/`— y
 * cada cámara aporta el suyo —`102`, `202`…—. Se concatena tal cual, sin
 * añadir ni quitar barras: así lo que se ve escrito en el formulario es
 * exactamente lo que se usa, y una barra de más o de menos es decisión de
 * quien configura, no una sorpresa del programa.
 */
export function componerEnlace(enlaceBase: string, canal: string): string {
  return `${enlaceBase.trim()}${canal.trim()}`;
}

/**
 * Convierte un enlace en la lista de reproducción que entiende el navegador.
 * El servidor de vídeo publica cada cámara como una carpeta con su
 * `index.m3u8` dentro; si alguien ya escribió el enlace completo, se respeta.
 */
export function comoPlaylistHls(enlace: string): string {
  const limpio = enlace.trim();
  if (limpio.toLowerCase().endsWith('.m3u8')) return limpio;

  return `${limpio.replace(/\/+$/, '')}/index.m3u8`;
}

/**
 * Nombre del camino con el que el servidor de vídeo publicará esta cámara.
 *
 * Lleva un sufijo aleatorio a propósito: quien conozca el camino puede ver el
 * vídeo, así que no debe poder adivinarse a partir del cliente ni del canal.
 * Se calcula una sola vez, al dar de alta la cámara, y se guarda.
 */
export function generarCamino(grabadorId: number, canal: string): string {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);

  const sufijo = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  const canalLimpio = canal.trim().replace(/[^a-zA-Z0-9]/g, '') || 'ch';

  return `dvr${grabadorId}_${canalLimpio}_${sufijo}`;
}

/** Motivo por el que un enlace no sirve. */
export type MotivoEnlaceInvalido = 'vacio' | 'malformado' | 'protocolo' | 'esRtsp' | 'noEsRtsp';

const MENSAJE: Readonly<Record<MotivoEnlaceInvalido, string>> = {
  vacio: 'Escribe el enlace del grabador, sin el número de cámara al final.',
  malformado: 'Ese enlace no es válido. Revisa que esté completo y bien escrito.',
  protocolo: 'El enlace debe empezar por https:// (o http:// si el servidor está en tu red).',
  esRtsp:
    'Eso es un RTSP. Para publicarlo automáticamente elige «Registrar por RTSP» arriba; aquí va el enlace que ya publica tu servidor.',
  noEsRtsp: 'Aquí va la dirección RTSP del grabador, la que empieza por rtsp://',
};

export function mensajeDeEnlaceInvalido(motivo: MotivoEnlaceInvalido): string {
  return MENSAJE[motivo];
}

/** Comprueba el enlace de un grabador ya publicado (modo manual). */
export function revisarEnlacePublicado(texto: string): MotivoEnlaceInvalido | null {
  const limpio = texto.trim();
  if (!limpio) return 'vacio';
  if (limpio.toLowerCase().startsWith('rtsp://')) return 'esRtsp';

  let url: URL;
  try {
    url = new URL(limpio);
  } catch {
    return 'malformado';
  }

  if (url.protocol !== 'https:' && url.protocol !== 'http:') return 'protocolo';
  return null;
}

/**
 * Comprueba la dirección RTSP del grabador (modo automático).
 *
 * Se rechaza que traiga usuario y contraseña dentro: van en sus propios
 * campos, porque la contraseña se guarda aparte y no vuelve a leerse. Meterla
 * en el enlace la dejaría a la vista de cualquiera que abra la pantalla.
 */
export function revisarEnlaceRtsp(texto: string): MotivoEnlaceInvalido | null {
  const limpio = texto.trim();
  if (!limpio) return 'vacio';
  if (!limpio.toLowerCase().startsWith('rtsp://')) return 'noEsRtsp';

  try {
    const url = new URL(limpio);
    if (!url.hostname) return 'malformado';
  } catch {
    return 'malformado';
  }

  return null;
}

/** `true` si el RTSP trae credenciales incrustadas. */
export function tieneCredencialesDentro(rtsp: string): boolean {
  return /^rtsp:\/\/[^/@]+@/i.test(rtsp.trim());
}

export interface CamaraVigilanciaProps {
  id: number;
  nombre: string;
  /** Número de canal en el grabador: 102, 202, 302… */
  canal: string;
  orden: number | null;
  activa: boolean;
  /** Camino con el que el servidor de vídeo la publica (modo automático). */
  camino: string | null;
  grabadorId: number;
  grabadorNombre: string;
  grabadorActivo: boolean;
  modo: ModoGrabador;
  /** Modo automático: RTSP del grabador sin canal ni credenciales. */
  rtspBase: string | null;
  /** Modo manual: enlace ya publicado, sin canal. */
  enlaceBase: string | null;
  /** Dónde publica el servidor de vídeo; viene de la configuración. */
  baseDeVideo: string;
  clienteId: number | null;
  clienteNombre: string | null;
  areaId: number | null;
  areaNombre: string | null;
}

/**
 * Una cámara tal como se ve en la parrilla de vigilancia.
 *
 * Su cliente y su área no son suyos: los hereda del grabador al que está
 * conectada, que es lo que físicamente ocurre.
 */
export class CamaraVigilancia {
  constructor(private readonly props: CamaraVigilanciaProps) {}

  get id(): number {
    return this.props.id;
  }

  get nombre(): string {
    return this.props.nombre;
  }

  get canal(): string {
    return this.props.canal;
  }

  get grabadorId(): number {
    return this.props.grabadorId;
  }

  get grabadorNombre(): string {
    return this.props.grabadorNombre;
  }

  get modo(): ModoGrabador {
    return this.props.modo;
  }

  get clienteId(): number | null {
    return this.props.clienteId;
  }

  get clienteNombre(): string | null {
    return this.props.clienteNombre;
  }

  get areaId(): number | null {
    return this.props.areaId;
  }

  get areaNombre(): string {
    return this.props.areaNombre ?? 'Sin área';
  }

  get orden(): number | null {
    return this.props.orden;
  }

  get activa(): boolean {
    return this.props.activa;
  }

  /** Emite sólo si la cámara y su grabador están activos. */
  get emite(): boolean {
    return this.props.activa && this.props.grabadorActivo && this.enlaceHls !== null;
  }

  /**
   * Dirección de la cámara en el grabador, para mostrar y para que el agente
   * sepa qué pedir. Nunca lleva credenciales: las pone el agente.
   */
  get origen(): string | null {
    if (this.props.modo === 'manual') {
      return this.props.enlaceBase ? componerEnlace(this.props.enlaceBase, this.canal) : null;
    }

    return this.props.rtspBase ? componerEnlace(this.props.rtspBase, this.canal) : null;
  }

  /**
   * Lo que reproduce el navegador.
   *
   * En automático sale del camino que publica el servidor de vídeo; hasta que
   * el agente lo crea, no hay nada que reproducir y devuelve `null`.
   */
  get enlaceHls(): string | null {
    if (this.props.modo === 'manual') {
      return this.props.enlaceBase
        ? comoPlaylistHls(componerEnlace(this.props.enlaceBase, this.canal))
        : null;
    }

    if (!this.props.camino || !this.props.baseDeVideo) return null;
    return `${this.props.baseDeVideo}/${this.props.camino}/index.m3u8`;
  }

  /** Explica por qué una cámara todavía no se puede ver. */
  get motivoSinSenal(): string | null {
    if (!this.props.grabadorActivo) return 'Su grabador está pausado';
    if (!this.props.activa) return 'La cámara está pausada';

    if (this.props.modo === 'automatico') {
      if (!this.props.baseDeVideo) return 'Falta configurar el servidor de vídeo';
      if (!this.props.camino) return 'El servidor de vídeo aún no la ha publicado';
    } else if (!this.props.enlaceBase) {
      return 'Al grabador le falta el enlace publicado';
    }

    return null;
  }

  coincideCon(texto: string): boolean {
    const t = texto.trim().toLowerCase();
    if (!t) return true;

    return [this.nombre, this.canal, this.areaNombre, this.grabadorNombre]
      .join(' ')
      .toLowerCase()
      .includes(t);
  }

  /** Orden de la parrilla: primero el que lo tenga puesto, luego por nombre. */
  static comparar(a: CamaraVigilancia, b: CamaraVigilancia): number {
    const oa = a.orden ?? Number.MAX_SAFE_INTEGER;
    const ob = b.orden ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;

    return a.nombre.localeCompare(b.nombre, 'es');
  }
}
